// SCR-STAFF-961 (AP-ENG-01؛ US-ENG-010): مشاوره فروش — نوع، مشاور، نیاز و انتظارات، خدمات یا پکیج‌های پیشنهادی با شبیه‌سازی قیمت CAT (CAT-072) و مرحله بعد؛
// «ایجاد برآورد هزینه» سبد را به BIL می‌برد (مراجعه‌کننده ثبت‌شده لازم است — C-AGENT-968)؛ پایان با نتیجه و پیگیری مرحله بعد (پیش‌فرض ۳ روز بعد).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type EngConsultationOutcome,
  type EngConsultationType,
  type EngConsultationView,
  type EngLeadLostReason,
  type EngOfferingOption,
  type EngProposalInput,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, FormDialog, LoadingState, Money, PageHeader, SelectRHF, StatusChip, TextFieldRHF } from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';
import { CONSULTATION_OUTCOMES, CONSULTATION_TYPES, consultationPath, DEFAULT_DUE_TIME, DueFields, engKeys, leadPath, localDate, LOST_REASONS, OfferingPicker, useLocalInstant } from './common';

export const SCR_STAFF_961 = 'SCR-STAFF-961';

interface ProposalRow {
  key: number;
  option: EngOfferingOption;
  quantity: number;
}

let rowKey = 0;

export function ConsultationPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const access = useAccess();
  const write = useWriteAccess('eng.consultation.manage');
  const idempotency = useIdempotencyKey();
  const leadId = search.get('leadId');
  const patientId = search.get('patientId');

  const existing = useQuery({
    queryKey: engKeys.consultation(session?.tenantId, id ?? 'new'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/consultations/{id}', { params: { path: { id: id! } } })),
    enabled: Boolean(id),
  });
  const view = existing.data;
  const editable = write.canWrite && (!view || view.status === 'Open');

  const [type, setType] = useState<EngConsultationType>('InPerson');
  const [needs, setNeeds] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [rows, setRows] = useState<ProposalRow[]>([]);
  const [picking, setPicking] = useState<EngOfferingOption | null>(null);
  const [completing, setCompleting] = useState(false);
  const [quoteError, setQuoteError] = useState<unknown>();

  useEffect(() => {
    if (view) {
      setType(view.type);
      setNeeds(view.needs ?? '');
      setNextStep(view.nextStep ?? '');
      setRows(
        view.proposals.map((item) => ({
          key: (rowKey += 1),
          option: { kind: item.packageId ? 'Package' : 'Service', id: (item.packageId ?? item.serviceId)!, name: item.title ?? '', price: null },
          quantity: Number(item.quantity),
        })),
      );
    }
  }, [view]);

  const items: EngProposalInput[] = useMemo(
    () => rows.map((row) => (row.option.kind === 'Package' ? { serviceId: null, packageId: row.option.id, quantity: row.quantity } : { serviceId: row.option.id, packageId: null, quantity: row.quantity })),
    [rows],
  );

  const simulation = useQuery({
    queryKey: [...engKeys.all(session?.tenantId), 'simulate', leadId ?? view?.leadId, patientId ?? view?.patientId, items],
    queryFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/engagement/consultations/simulate', {
          body: { leadId: view?.leadId ?? leadId, patientId: view?.patientId ?? patientId, branchId: view?.branchId ?? null, items },
        }),
      ),
    enabled: items.length > 0 && editable,
    placeholderData: keepPreviousData,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        leadId: view ? null : leadId,
        patientId: view ? null : patientId,
        type,
        consultantMembershipId: null,
        branchId: view?.branchId ?? null,
        needs: needs || null,
        items,
        nextStep: nextStep || null,
        nextStepAt: null,
        appointmentId: view?.appointmentId ?? null,
        rowVersion: view?.rowVersion ?? null,
      };
      return unwrap(
        view
          ? tenantApi.PUT('/api/v1/engagement/consultations/{id}', { params: { path: { id: view.id } }, body })
          : tenantApi.POST('/api/v1/engagement/consultations', { body, headers: { 'Idempotency-Key': idempotency.current() } }),
      );
    },
    onSettled: (_data, error) => {
      if (!view) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(engKeys.consultation(session?.tenantId, saved.id), saved);
      void queryClient.invalidateQueries({ queryKey: engKeys.leads(session?.tenantId) });
      if (!view) {
        navigate(consultationPath(saved.id), { replace: true });
      }
    },
  });

  const quote = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/engagement/consultations/{id}:create-quote', { params: { path: { id: view!.id } }, body: { title: t('eng:consultation.quote_title'), notes: null } })),
    onMutate: () => setQuoteError(undefined),
    onSuccess: (saved) => {
      queryClient.setQueryData(engKeys.consultation(session?.tenantId, saved.id), saved);
      void queryClient.invalidateQueries({ queryKey: engKeys.all(session?.tenantId) });
    },
    onError: setQuoteError,
  });

  if (id && existing.isPending) {
    return <LoadingState rows={4} />;
  }

  if (existing.error) {
    return <ErrorState error={existing.error} onRetry={() => void existing.refetch()} />;
  }

  if (!id && !leadId && !patientId) {
    return <ErrorState error={new Error('subject')} />;
  }

  const subjectName = view?.leadName ?? view?.patientName;
  const totals = editable && items.length > 0 ? simulation.data : undefined;
  const priced = totals?.items ?? view?.proposals ?? [];
  const hasPatient = Boolean(view?.patientId);

  return (
    <section data-screen-id={SCR_STAFF_961}>
      <PageHeader
        titleKey={view ? 'eng:consultation.title' : 'eng:consultation.new_title'}
        titleValues={{ name: subjectName ?? '' }}
        badges={view && <StatusChip tone={view.status === 'Open' ? 'booked' : 'done'} labelKey={`eng:consultation_status.${view.status}`} />}
        actions={
          <>
            {view?.leadId && (
              <Button component={RouterLink} to={leadPath(view.leadId)}>
                {t('eng:consultation.back_to_lead')}
              </Button>
            )}
            {editable && (
              <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending} data-testid="consultation-save">
                {t('eng:consultation.save')}
              </Button>
            )}
            {view?.status === 'Open' && write.canWrite && (
              <Button onClick={() => setCompleting(true)} data-testid="consultation-complete">
                {t('eng:consultation.complete')}
              </Button>
            )}
          </>
        }
      />
      <Stack spacing={2}>
        {save.error && <ErrorState error={save.error} />}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select size="small" label={t('eng:consultation.type')} value={type} onChange={(event) => setType(event.target.value as EngConsultationType)} disabled={!editable} sx={{ minWidth: 180 }} inputProps={{ 'data-testid': 'consultation-type' }}>
                {CONSULTATION_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`eng:consultation_type.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
              {view && <Typography variant="body2" sx={{ alignSelf: 'center' }}>{t('eng:consultation.consultant', { name: view.consultantName ?? '—' })}</Typography>}
            </Stack>
            <TextField
              label={t('eng:consultation.needs')}
              helperText={t('eng:common.no_clinical_hint')}
              value={needs}
              onChange={(event) => setNeeds(event.target.value)}
              disabled={!editable}
              multiline
              minRows={2}
              inputProps={{ maxLength: 2000, 'data-testid': 'consultation-needs' }}
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            {t('eng:consultation.proposals')}
          </Typography>
          {editable && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <OfferingPicker value={picking} onChange={setPicking} labelKey="eng:consultation.add_item" testId="consultation-offering" packages />
              <Button
                startIcon={<AddIcon />}
                disabled={!picking}
                onClick={() => {
                  if (picking) {
                    setRows((current) => [...current, { key: (rowKey += 1), option: picking, quantity: 1 }]);
                    setPicking(null);
                  }
                }}
                data-testid="consultation-add-item"
              >
                {t('eng:consultation.add')}
              </Button>
            </Stack>
          )}
          <Table size="small" aria-label={t('eng:consultation.proposals')} data-testid="consultation-proposals">
            <TableHead>
              <TableRow>
                <TableCell>{t('eng:consultation.item')}</TableCell>
                <TableCell>{t('eng:consultation.quantity')}</TableCell>
                <TableCell>{t('eng:consultation.gross')}</TableCell>
                <TableCell>{t('eng:consultation.discount')}</TableCell>
                <TableCell>{t('eng:consultation.payable')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const price = priced[index];
                return (
                  <TableRow key={row.key}>
                    <TableCell>{row.option.name}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={row.quantity}
                        disabled={!editable}
                        onChange={(event) => {
                          const quantity = Math.max(1, Math.min(100, Number(event.target.value) || 1));
                          setRows((current) => current.map((candidate) => (candidate.key === row.key ? { ...candidate, quantity } : candidate)));
                        }}
                        inputProps={{ min: 1, max: 100, 'aria-label': t('eng:consultation.quantity'), 'data-testid': `consultation-qty-${index}` }}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell>{price ? <Money amountMinor={Number(price.gross.amount)} /> : '—'}</TableCell>
                    <TableCell>{price ? <Money amountMinor={Number(price.discount.amount)} /> : '—'}</TableCell>
                    <TableCell>{price ? (price.priceError ? t('eng:consultation.no_price') : <Money amountMinor={Number(price.payable.amount)} />) : '—'}</TableCell>
                    <TableCell>
                      {editable && (
                        <IconButton aria-label={t('eng:consultation.remove')} onClick={() => setRows((current) => current.filter((candidate) => candidate.key !== row.key))}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }} data-testid="consultation-total">
            <Typography variant="subtitle2">
              {t('eng:consultation.total')}: <Money amountMinor={Number((totals?.payable ?? view?.simulatedPayable)?.amount ?? 0)} />
            </Typography>
            {rows.length > 0 && <Typography variant="caption" color="text.secondary">{t('eng:consultation.simulated_note')}</Typography>}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <TextField
            label={t('eng:consultation.next_step')}
            value={nextStep}
            onChange={(event) => setNextStep(event.target.value)}
            disabled={!editable}
            fullWidth
            inputProps={{ maxLength: 500, 'data-testid': 'consultation-next-step' }}
          />
        </Paper>

        {view && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h3">{t('eng:consultation.quote')}</Typography>
              {view.quoteId ? (
                <>
                  <Typography variant="body2" data-testid="consultation-quote-status">
                    {t('eng:consultation.quote_status', { status: t(`eng:quote_status.${view.quoteStatus ?? 'Draft'}`) })}
                  </Typography>
                  <Button component={RouterLink} to={`/quotes/${view.quoteId}`} data-testid="consultation-open-quote">
                    {t('eng:consultation.open_quote')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => quote.mutate()}
                  disabled={!hasPatient || view.proposals.length === 0 || !access.can('bil.quote.create') || quote.isPending}
                  data-testid="consultation-create-quote"
                >
                  {t('eng:consultation.create_quote')}
                </Button>
              )}
            </Stack>
            {!hasPatient && !view.quoteId && (
              <Alert severity="info" sx={{ mt: 1 }} data-testid="consultation-patient-required">
                {t('eng:consultation.patient_required')}
              </Alert>
            )}
            {quoteError !== undefined && <ErrorState error={quoteError} />}
          </Paper>
        )}
        {view?.outcome && (
          <Typography variant="body2" data-testid="consultation-outcome">
            {t('eng:consultation.outcome_label')}: {t(`eng:consultation_outcome.${view.outcome}`)}
            {view.outcomeReason ? ` — ${t(`eng:lost_reason.${view.outcomeReason}`)}${view.outcomeNote ? `: ${view.outcomeNote}` : ''}` : ''}
          </Typography>
        )}
        {view && view.proposals.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('eng:consultation.items_count', { count: view.proposals.length, value: faDigits(view.proposals.length) })}
          </Typography>
        )}
      </Stack>
      {completing && view && (
        <CompleteConsultationDialog
          consultation={view}
          onClose={(saved) => {
            setCompleting(false);
            if (saved) {
              queryClient.setQueryData(engKeys.consultation(session?.tenantId, saved.id), saved);
              void queryClient.invalidateQueries({ queryKey: engKeys.all(session?.tenantId) });
            }
          }}
        />
      )}
    </section>
  );
}

const completeSchema = z
  .object({
    outcome: z.string(),
    reason: z.string(),
    note: z.string().max(1000, { error: 'common:validation.max_length' }),
    followUp: z.enum(['yes', 'no']),
    date: z.string(),
    time: z.string(),
  })
  .superRefine((value, context) => {
    // AC-ENG-011.1: «تبدیل نشد» و «لغو شد» علت از فهرست می‌خواهند و «سایر» یادداشت.
    if ((value.outcome === 'NotConverted' || value.outcome === 'Cancelled') && !value.reason) {
      context.addIssue({ code: 'custom', path: ['reason'], message: 'eng:validation.outcome_reason_required' });
    }
    if ((value.outcome === 'NotConverted' || value.outcome === 'Cancelled') && value.reason === 'Other' && !value.note.trim()) {
      context.addIssue({ code: 'custom', path: ['note'], message: 'eng:validation.lost_note_required' });
    }
  });
type CompleteForm = z.input<typeof completeSchema>;

/** پایان مشاوره با نتیجه، علت عدم تبدیل یا لغو (AC-ENG-011.1) و پیگیری «مرحله بعد» (پیش‌فرض ۳ روز بعد؛ «در حال فکر» بدون موعد ← پیگیری خودکار). */
function CompleteConsultationDialog({ consultation, onClose }: { consultation: EngConsultationView; onClose: (saved?: EngConsultationView) => void }) {
  const { t } = useTranslation('eng');
  const { tenantApi } = useTenantSession();
  const toInstant = useLocalInstant();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState } = useForm<CompleteForm>({
    resolver: zodResolver(completeSchema),
    defaultValues: { outcome: '', reason: '', note: '', followUp: consultation.nextStep ? 'yes' : 'no', date: localDate(3), time: DEFAULT_DUE_TIME },
  });
  const followUp = useWatch({ control, name: 'followUp' });
  const outcome = useWatch({ control, name: 'outcome' });
  const needsReason = outcome === 'NotConverted' || outcome === 'Cancelled';
  const save = useMutation({
    mutationFn: (form: CompleteForm) =>
      unwrap(
        tenantApi.POST('/api/v1/engagement/consultations/{id}:complete', {
          params: { path: { id: consultation.id } },
          body: {
            outcome: (form.outcome || null) as EngConsultationOutcome | null,
            followUpDueAt: form.followUp === 'yes' && form.date ? toInstant(form.date, form.time) : null,
            outcomeReason: needsReason ? ((form.reason || null) as EngLeadLostReason | null) : null,
            outcomeNote: needsReason ? form.note.trim() || null : null,
          },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: (saved) => onClose(saved),
    onError: setSubmitError,
  });
  return (
    <FormDialog
      open
      maxWidth="sm"
      screenId="SCR-STAFF-961-DONE"
      titleKey="eng:consultation.complete"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <SelectRHF
          control={control}
          name="outcome"
          labelKey="eng:consultation.outcome_label"
          fullWidth
          options={[{ value: '', labelKey: 'eng:common.unknown' }, ...CONSULTATION_OUTCOMES.map((value) => ({ value, labelKey: `eng:consultation_outcome.${value}` }))]}
        />
        {needsReason && (
          <>
            <SelectRHF
              control={control}
              name="reason"
              labelKey="eng:consultation.outcome_reason"
              required
              fullWidth
              options={LOST_REASONS.map((value) => ({ value, labelKey: `eng:lost_reason.${value}` }))}
            />
            <TextFieldRHF control={control} name="note" labelKey="eng:consultation.outcome_note" multiline minRows={2} fullWidth />
          </>
        )}
        {outcome === 'ThinkingAbout' && !consultation.nextStep && <Alert severity="info">{t('eng:consultation.thinking_hint')}</Alert>}
        {consultation.nextStep ? (
          <>
            <SelectRHF
              control={control}
              name="followUp"
              labelKey="eng:consultation.follow_up"
              fullWidth
              options={[
                { value: 'yes', labelKey: 'eng:consultation.follow_up_yes' },
                { value: 'no', labelKey: 'eng:consultation.follow_up_no' },
              ]}
            />
            {followUp === 'yes' && <DueFields control={control} dateName="date" timeName="time" testId="consultation-followup-due" />}
          </>
        ) : null}
      </Stack>
    </FormDialog>
  );
}
